import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { usePrompt } from '@/contexts/PromptContext'

interface RoleData {
  generated_roles: string[]
  selected_role: string
}

interface RoleStepProps {
  value?: RoleData
  onChange?: (value: RoleData) => void
  readOnly?: boolean
}

const RoleStep: React.FC<RoleStepProps> = ({
  value: externalValue,
  onChange: externalOnChange,
  readOnly = false
}) => {
  const { t } = useTranslation('steps')
  const [internalValue, setInternalValue] = useState<RoleData>({ generated_roles: [], selected_role: '' })
  const [hasRefreshed, setHasRefreshed] = useState(false)
  const { promptData, updatePrompt, loading, refreshPrompt } = usePrompt()
  
  // Use external value if provided (for refine mode), otherwise use internal state
  const value = externalValue !== undefined ? externalValue : internalValue
  const setValue = externalOnChange || setInternalValue

  // Load existing role data when promptData is available
  useEffect(() => {
    if (promptData?.role && !externalValue) {
      console.log('RoleStep: Updating internal value with promptData.role:', promptData.role)
      setInternalValue(promptData.role)
    }
  }, [promptData?.role, externalValue])

  // Add a refresh mechanism when component mounts and no role data is available
  useEffect(() => {
    if (!externalValue && promptData && (!promptData.role || promptData.role.generated_roles.length === 0) && !hasRefreshed) {
      console.log('RoleStep: No role data available, refreshing prompt data...')
      setHasRefreshed(true)
      refreshPrompt().catch(error => {
        console.error('Failed to refresh prompt data:', error)
      })
    }
  }, [promptData, externalValue, hasRefreshed, refreshPrompt])

  // Update role immediately without debouncing
  const updateRole = async (newRoleData: RoleData) => {
    try {
      await updatePrompt({ role: newRoleData })
    } catch (error) {
      console.error('Error updating role:', error)
    }
  }

  const handleRoleSelect = (role: string) => {
    if (readOnly) return // Don't allow changes in read-only mode
    
    // Create new role data with the selected role
    const newRoleData = { ...value, selected_role: role }
    setValue(newRoleData)
    
    // If we're in refine mode (external onChange), call the external handler
    if (externalOnChange) {
      externalOnChange(newRoleData)
      return
    }
    
    // Update database immediately
    updateRole(newRoleData)
  }

  return (
    <div className="text-center">
      <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-4 mt-2 sm:mt-6 px-4">
        {t('role.title')}
      </h2>

      <p className="text-gray-600 mb-6 sm:mb-8 max-w-lg mx-auto px-4 text-xs sm:text-base">
      {t('role.description')}
      </p>

      {value.generated_roles.length > 0 ? (
        <div className="max-w-md mx-auto space-y-2 sm:space-y-3 mb-6 sm:mb-8 px-4">
          {value.generated_roles.map((role) => (
            <button
              key={role}
              onClick={() => handleRoleSelect(role)}
              disabled={readOnly || loading}
              className={`w-full p-2 sm:p-4 text-left rounded-lg border-2 transition-colors ${
                value.selected_role === role 
                  ? 'border-gray-800 bg-gray-50' 
                  : 'border-gray-200 hover:border-gray-300'
              } ${readOnly || loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-center">
                {value.selected_role === role && (
                  <span className="text-green-600 mr-2 sm:mr-3 text-base sm:text-xl">✓</span>
                )}
                <span className={`text-sm sm:text-base ${value.selected_role === role ? 'font-medium' : ''}`}>
                  {role}
                </span>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="text-center text-xs sm:text-sm text-gray-500 mb-8">
          {loading ? t('role.loading') : t('role.generating')}
        </div>
      )}
    </div>
  )
}

export default RoleStep
